import React, { useState } from 'react';
import { useI18n } from '../i18n';
import { getLocalDateISO, isoToDateInput, formatarMinutos, getCategoryName, getMemberHex } from '../utils';
import PhotoPicker from './PhotoPicker';
import { Button, Field, Input, Modal, Notice, Select, Textarea, cx } from './ui';

const TIME_SHORTCUTS = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60];

// type: 'tarefa' | 'membro' | 'registrar'. `item` is the row being edited, or
// null when creating. Each action resolves to true on success; the modal stays
// open (keeping what was typed) when a save fails.
export default function FormModal({ type, item, data, defaultMembroId, actions, onClose }) {
  const { t } = useI18n();
  const [saving, setSaving] = useState(false);
  const [foto, setFoto] = useState({ file: null, remove: false });
  const editingRegistro = type === 'registrar' ? item : null;
  const [formData, setFormData] = useState({
    nome: item?.nome || '',
    categoriaId: item?.categoria_id || data.categorias[0]?.id || 1,
    peso: item?.peso || 1,
    membroId: editingRegistro ? editingRegistro.membro_id : (defaultMembroId || data.membros[0]?.id || ''),
    tarefaId: editingRegistro ? editingRegistro.tarefa_id : (data.tarefas[0]?.id || ''),
    tempoMinutos: editingRegistro ? editingRegistro.tempo_minutos : '',
    dataRegistro: editingRegistro ? isoToDateInput(editingRegistro.criado_em) : getLocalDateISO(),
    comentario: editingRegistro?.comentario || '',
    genero: item?.genero || '',
    dataNascimento: item?.data_nascimento || '',
    regimeTrabalho: item?.regime_trabalho || ''
  });
  const set = (campo) => (e) => setFormData(prev => ({ ...prev, [campo]: e.target.value }));
  const setNumber = (campo) => (e) => setFormData(prev => ({ ...prev, [campo]: parseInt(e.target.value, 10) || '' }));

  const semDadosParaRegistrar = type === 'registrar' && (data.membros.length === 0 || data.tarefas.length === 0);

  const valido = type === 'tarefa'
    ? Boolean(formData.nome.trim()) && parseFloat(formData.peso) > 0
    : type === 'membro'
      ? Boolean(formData.nome.trim())
      : Boolean(formData.membroId && formData.tarefaId && formData.tempoMinutos > 0 && formData.dataRegistro);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || !valido || semDadosParaRegistrar) return;

    let salvar;
    if (type === 'tarefa') {
      salvar = item
        ? () => actions.editarTarefa(item.id, formData.nome.trim(), formData.categoriaId, formData.peso)
        : () => actions.adicionarTarefa(formData.nome.trim(), formData.categoriaId, formData.peso);
    } else if (type === 'membro') {
      const campos = [formData.nome.trim(), formData.genero || null, formData.dataNascimento || null, formData.regimeTrabalho || null, foto];
      salvar = item
        ? () => actions.editarMembro(item.id, ...campos)
        : () => actions.adicionarMembro(...campos);
    } else if (item) {
      // preserve the original time-of-day; only the date is editable here
      const [y, mo, d] = formData.dataRegistro.split('-').map(Number);
      const novaData = new Date(item.criado_em);
      novaData.setFullYear(y, mo - 1, d);
      salvar = () => actions.atualizarRegistro(item.id, formData.membroId, formData.tarefaId, formData.tempoMinutos, novaData.toISOString(), formData.comentario);
    } else {
      const localTimestamp = new Date(`${formData.dataRegistro}T12:00:00`).toISOString();
      salvar = () => actions.registrarAtividade(formData.membroId, formData.tarefaId, formData.tempoMinutos, localTimestamp, formData.comentario);
    }

    setSaving(true);
    const ok = await salvar();
    if (ok) onClose();
    else setSaving(false);
  };

  const tarefaSelecionada = data.tarefas.find(tf => tf.id === parseInt(formData.tarefaId, 10));
  const minutosTotais = tarefaSelecionada && formData.tempoMinutos ? formData.tempoMinutos * tarefaSelecionada.peso : 0;
  const membrosOrdenados = [...data.membros].sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
  const tarefasOrdenadas = [...data.tarefas].sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));

  const title = type === 'tarefa'
    ? (item ? t('modal.editTask') : t('modal.newTaskTitle'))
    : type === 'membro'
      ? (item ? t('modal.editMember') : t('modal.newMemberTitle'))
      : (item ? t('modal.editActivity') : t('modal.registerActivity'));

  const submitLabel = type === 'tarefa'
    ? (item ? t('modal.saveChanges') : t('modal.addTask'))
    : type === 'membro'
      ? (item ? t('modal.saveChanges') : t('modal.addMember'))
      : (item ? t('modal.saveChanges') : t('modal.registerActivity'));

  const footer = (
    <>
      <Button type="button" variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
      <Button type="submit" form="modal-form" disabled={saving || !valido || semDadosParaRegistrar}>
        {saving ? t('common.saving') : submitLabel}
      </Button>
    </>
  );

  return (
    <Modal title={title} onClose={onClose} footer={footer} closeLabel={t('common.close')}>
      <form id="modal-form" onSubmit={handleSubmit} noValidate className="space-y-4">
        {semDadosParaRegistrar ? (
          <Notice tone="warning">{t('modal.needMemberAndTask')}</Notice>
        ) : type === 'tarefa' ? (
          <>
            <Field label={t('modal.taskNameLabel')} htmlFor="modal-tarefa-nome" required>
              <Input id="modal-tarefa-nome" type="text" value={formData.nome} onChange={set('nome')} placeholder={t('modal.taskNamePlaceholder')} autoFocus />
            </Field>
            <Field label={t('modal.categoryLabel')} htmlFor="modal-categoria" required>
              <Select id="modal-categoria" value={formData.categoriaId} onChange={(e) => setFormData(prev => ({ ...prev, categoriaId: parseInt(e.target.value, 10) }))}>
                {data.categorias.map(cat => (<option key={cat.id} value={cat.id}>{getCategoryName(cat, t)}</option>))}
              </Select>
            </Field>
            <Field label={t('modal.weightLabel')} htmlFor="modal-peso" help={t('modal.weightHelp')} required>
              <Input id="modal-peso" type="number" step="0.5" min="0.5" value={formData.peso} onChange={set('peso')} />
            </Field>
          </>
        ) : type === 'membro' ? (
          <>
            <PhotoPicker
              src={foto.remove ? null : item?.avatar_url}
              name={formData.nome || item?.nome}
              color={item ? getMemberHex(item.id, data.membros) : '#737373'}
              square
              onSelect={(file) => setFoto({ file, remove: false })}
              onRemove={() => setFoto({ file: null, remove: true })}
            />
            <Field label={t('modal.memberNameLabel')} htmlFor="modal-membro-nome" required>
              <Input id="modal-membro-nome" type="text" value={formData.nome} onChange={set('nome')} placeholder={t('modal.memberNamePlaceholder')} />
            </Field>
            <Field label={t('modal.genderLabel')} htmlFor="modal-genero" help={t('modal.genderHelp')}>
              <Select id="modal-genero" value={formData.genero} onChange={set('genero')}>
                <option value="">{t('common.select')}</option>
                <option value="homem">{t('modal.genderMale')}</option>
                <option value="mulher">{t('modal.genderFemale')}</option>
                <option value="nao_binario">{t('modal.genderNonBinary')}</option>
                <option value="prefiro_nao_declarar">{t('common.preferNotToDeclare')}</option>
              </Select>
            </Field>
            <Field label={t('modal.birthdateLabel')} htmlFor="modal-nascimento" help={t('modal.birthdateHelp')}>
              <Input id="modal-nascimento" type="date" value={formData.dataNascimento} onChange={set('dataNascimento')} />
            </Field>
            <Field label={t('modal.workRegimeLabel')} htmlFor="modal-regime" help={t('modal.workRegimeHelp')}>
              <Select id="modal-regime" value={formData.regimeTrabalho} onChange={set('regimeTrabalho')}>
                <option value="">{t('common.select')}</option>
                <option value="remoto">{t('modal.workRemote')}</option>
                <option value="externo">{t('modal.workExternal')}</option>
                <option value="hibrido">{t('modal.workHybrid')}</option>
              </Select>
            </Field>
            <Notice>
              <span className="text-xs" dangerouslySetInnerHTML={{ __html: t('modal.privacyNotice') }} />
            </Notice>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label={t('modal.whoDidIt')} htmlFor="modal-quem" required>
                <Select id="modal-quem" value={formData.membroId} onChange={(e) => setFormData(prev => ({ ...prev, membroId: parseInt(e.target.value, 10) }))}>
                  {membrosOrdenados.map(membro => (<option key={membro.id} value={membro.id}>{membro.nome}</option>))}
                </Select>
              </Field>
              <Field label={t('modal.whichTask')} htmlFor="modal-tarefa" required>
                <Select id="modal-tarefa" value={formData.tarefaId} onChange={(e) => setFormData(prev => ({ ...prev, tarefaId: parseInt(e.target.value, 10) }))}>
                  {tarefasOrdenadas.map(tarefa => (<option key={tarefa.id} value={tarefa.id}>{t('modal.taskWithWeight', { name: tarefa.nome, weight: tarefa.peso })}</option>))}
                </Select>
              </Field>
            </div>
            <Field label={t('modal.howMuchTime')} htmlFor="modal-tempo" help={t('modal.timeHelp')} required>
              <Input id="modal-tempo" type="number" step="5" min="5" value={formData.tempoMinutos} onChange={setNumber('tempoMinutos')} placeholder={t('modal.timePlaceholder')} />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {TIME_SHORTCUTS.map(minutes => (
                  <button
                    key={minutes}
                    type="button"
                    aria-pressed={formData.tempoMinutos === minutes}
                    onClick={() => setFormData(prev => ({ ...prev, tempoMinutos: minutes }))}
                    className={cx('h-7 px-2.5 rounded-md text-xs font-medium border transition-colors tabular-nums', formData.tempoMinutos === minutes ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50')}
                  >
                    {t('common.minutesShort', { count: minutes })}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={t('modal.whenLabel')} htmlFor="modal-data" help={t('modal.whenHelp')} required>
              <Input id="modal-data" type="date" value={formData.dataRegistro} max={getLocalDateISO()} onChange={(e) => setFormData(prev => ({ ...prev, dataRegistro: e.target.value || getLocalDateISO() }))} />
            </Field>
            <Field label={t('modal.commentLabel')} htmlFor="modal-comentario" help={t('modal.commentHelp')}>
              <Textarea id="modal-comentario" value={formData.comentario} onChange={set('comentario')} maxLength={500} rows={2} placeholder={t('modal.commentPlaceholder')} />
            </Field>
            {tarefaSelecionada && formData.tempoMinutos ? (
              <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-gray-50 text-sm">
                <span className="text-gray-600" dangerouslySetInnerHTML={{ __html: t('modal.calculation', { minutes: formData.tempoMinutos, weight: tarefaSelecionada.peso }) }} />
                <span className="font-semibold text-gray-900 tabular-nums">{formatarMinutos(minutosTotais)}</span>
              </div>
            ) : null}
          </>
        )}
      </form>
    </Modal>
  );
}
